/**
 * Rate-sheet price sources and their versioned grids (PriceSourcesController on Skynet.Pricing).
 * Company-scoped by JWT, gated by FeedAdminPolicy.Access. Every call skips the global
 * forbidden-reload so a missing claim surfaces inline through the calling action.
 */
import { SKIP_FORBIDDEN } from './httpConfig';

export class PriceSourcesApi {
  #http;

  constructor(http) {
    this.#http = http;
  }

  async getSources() {
    return this.#http.get('/api/price-sources', SKIP_FORBIDDEN);
  }

  // The pricing workbench reads this under its own policy (PricingWorkbenchController), so a user
  // without feed access still learns whether the company prices from a rate sheet.
  async getRateSheetSourceAvailability() {
    return this.#http.get('/api/pricing-workbench/rate-sheet-source', SKIP_FORBIDDEN);
  }

  async getSource(priceSourceId) {
    return this.#http.get(`/api/price-sources/${priceSourceId}`, SKIP_FORBIDDEN);
  }

  async createSource(body) {
    return this.#http.post('/api/price-sources', body, SKIP_FORBIDDEN);
  }

  async updateSource(priceSourceId, body) {
    return this.#http.put(`/api/price-sources/${priceSourceId}`, body, SKIP_FORBIDDEN);
  }

  async deleteSource(priceSourceId) {
    return this.#http.delete(`/api/price-sources/${priceSourceId}`, SKIP_FORBIDDEN);
  }

  async getProgramCandidates(priceSourceId) {
    return this.#http.get(`/api/price-sources/${priceSourceId}/program-candidates`, SKIP_FORBIDDEN);
  }

  async getVersions(priceSourceId, { take } = {}) {
    return this.#http.get(`/api/price-sources/${priceSourceId}/versions`, {
      ...SKIP_FORBIDDEN,
      params: { take },
    });
  }

  async getVersion(priceSourceId, versionId) {
    return this.#http.get(
      `/api/price-sources/${priceSourceId}/versions/${versionId}`,
      SKIP_FORBIDDEN,
    );
  }

  async getTemplates(priceSourceId, { take } = {}) {
    return this.#http.get(`/api/price-sources/${priceSourceId}/templates`, {
      ...SKIP_FORBIDDEN,
      params: { take },
    });
  }

  async getActiveTemplate(priceSourceId) {
    return this.#http.get(`/api/price-sources/${priceSourceId}/templates/active`, SKIP_FORBIDDEN);
  }

  async getMatrixVersions(priceSourceId, { take } = {}) {
    return this.#http.get(`/api/price-sources/${priceSourceId}/matrix-versions`, {
      ...SKIP_FORBIDDEN,
      params: { take },
    });
  }

  async getMatrixVersion(priceSourceId, versionId) {
    return this.#http.get(
      `/api/price-sources/${priceSourceId}/matrix-versions/${versionId}`,
      SKIP_FORBIDDEN,
    );
  }

  async deactivateMatrixVersion(priceSourceId, versionId) {
    return this.#http.post(
      `/api/price-sources/${priceSourceId}/matrix-versions/${versionId}/deactivate`,
      null,
      SKIP_FORBIDDEN,
    );
  }

  // The version's investor SRP as the SRP explorer matrix (states by loan amount, in bps). Both
  // filters are optional server-side; loan amounts repeat the key, as ASP.NET binds an array.
  async getVersionSrpMatrix(priceSourceId, versionId, { product, loanAmounts } = {}) {
    return this.#http.get(`/api/price-sources/${priceSourceId}/versions/${versionId}/srp-matrix`, {
      ...SKIP_FORBIDDEN,
      params: { product, loanAmounts },
      paramsSerializer: { indexes: null },
    });
  }

  async createVersion(priceSourceId, body) {
    return this.#http.post(`/api/price-sources/${priceSourceId}/versions`, body, SKIP_FORBIDDEN);
  }

  async republishVersion(priceSourceId, body) {
    return this.#http.post(
      `/api/price-sources/${priceSourceId}/versions/republish`,
      body,
      SKIP_FORBIDDEN,
    );
  }

  async promoteVersion(priceSourceId, versionId) {
    return this.#http.post(
      `/api/price-sources/${priceSourceId}/versions/${versionId}/promote`,
      null,
      SKIP_FORBIDDEN,
    );
  }

  async discardVersion(priceSourceId, versionId) {
    return this.#http.post(
      `/api/price-sources/${priceSourceId}/versions/${versionId}/discard`,
      null,
      SKIP_FORBIDDEN,
    );
  }
}
